import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Input, Switch } from 'antd'
import { HiCheck } from 'react-icons/hi'
import { Section, CommonButton } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import { useLoyaltySettings } from './hooks/useLoyaltySettings'
import { Card, FormLabel, FormItemTitle, FormItemDescription } from './styles'

interface LoyaltySectionProps {
  stepStatus: StepStatus
}

const LoyaltySection = ({ stepStatus }: LoyaltySectionProps) => {
  const { loyaltySettings, isLoading, isSaving, saveLoyaltySettings } =
    useLoyaltySettings()
  const { addMessage } = useMessageStore()

  const [password, setPassword] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [hasSeededEnabled, setHasSeededEnabled] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const savedEnabled = !!loyaltySettings?.lightspeedLoyaltyEnabled
  const username = loyaltySettings?.lightspeedLoyaltyUsername || ''
  const passwordSet = !!loyaltySettings?.lightspeedLoyaltyPasswordSet

  // Validation looks only at the field: while loyalty is on, a non-blank
  // password must be entered to save. `passwordSet` drives the checkmark only.
  const needsPassword = enabled && !password.trim()

  useEffect(() => {
    if (isLoading || hasSeededEnabled) return

    setEnabled(savedEnabled)
    setHasSeededEnabled(true)
  }, [isLoading, hasSeededEnabled, savedEnabled])

  const handleToggle = (checked: boolean) => {
    setEnabled(checked)
    setPasswordError(null)

    if (!checked) {
      setPassword('')
    }
  }

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    setPasswordError(null)
  }

  const handleSave = async () => {
    if (needsPassword) {
      setPasswordError('Password is required')
      return
    }

    try {
      await saveLoyaltySettings({
        enabled,
        password: password || undefined
      })
      setPassword('')
      addMessage({
        type: 'message',
        content: 'Authentication details saved successfully!'
      })
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to save authentication details!'
      })
    }
  }

  return (
    <Section
      title='Connect Loyalty'
      description='Use Slerp to power your in-store loyalty, giving you a single loyalty scheme across both in-store and online customers.'
      stepStatus={stepStatus}
      stepNumber={3}
      action={
        <Switch
          checked={enabled}
          loading={isLoading}
          disabled={isSaving}
          onChange={handleToggle}
        />
      }
    >
      {enabled && (
        <Card gap={16} marginBottom={0} borderColor='#d2d2d9'>
          <FormLabel marginBottom={0}>
            <FormItemTitle>Authentication</FormItemTitle>
            <FormItemDescription>
              Set a password to authenticate the integration using the username
              below. Your password will be encrypted and can&rsquo;t be read
              afterwards, so make a note of it.
            </FormItemDescription>
          </FormLabel>

          <Divider />

          <FieldsRow>
            <div>
              <FormLabel>
                <FormItemTitle>User name</FormItemTitle>
              </FormLabel>
              <Input
                name='lightspeed-loyalty-user'
                value={username}
                readOnly
                disabled={isLoading || isSaving}
                autoComplete='off'
                data-lpignore='true'
                data-1p-ignore='true'
                data-form-type='other'
              />
            </div>

            <div>
              <FormLabel>
                <TitleRow>
                  <FormItemTitle>Password</FormItemTitle>
                  {passwordSet && (
                    <SavedIcon data-testid='password-saved-icon'>
                      <HiCheck />
                    </SavedIcon>
                  )}
                </TitleRow>
              </FormLabel>
              <PasswordInput
                name='lightspeed-loyalty-secret'
                value={password}
                onChange={handleChangePassword}
                placeholder={passwordSet ? '**********' : 'Enter password'}
                disabled={isLoading || isSaving}
                status={passwordError ? 'error' : undefined}
                autoComplete='new-password'
                data-lpignore='true'
                data-1p-ignore='true'
                data-form-type='other'
              />
              {passwordError && (
                <ErrorText role='alert'>{passwordError}</ErrorText>
              )}
            </div>
          </FieldsRow>
        </Card>
      )}

      <Actions>
        <CommonButton onClick={handleSave} disabled={isSaving}>
          Save
        </CommonButton>
      </Actions>
    </Section>
  )
}

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const SavedIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 100%;
  background: #d092dd;

  svg {
    width: 8px;
    height: 8px;
    fill: #ffffff;
  }
`

const FieldsRow = styled.div`
  display: flex;
  gap: 16px;

  > div {
    flex: 1;
  }
`

const PasswordInput = styled(Input.Password)`
  &.ant-input-affix-wrapper:hover,
  &.ant-input-affix-wrapper:focus,
  &.ant-input-affix-wrapper-focused {
    border-right-width: 1px !important;
  }
`

const ErrorText = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #a40000;
`

const Divider = styled.div`
  height: 1px;
  background: #e7e5e9;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`

export default LoyaltySection
